import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabCommitMergeResult } from '@common/collab/models/collab-commit-result.model';
import { CollabConflictResolutionDialogComponent, CollabConflictResolutionDialogData } from '@portal-core/collab/components/collab-conflict-resolution-dialog/collab-conflict-resolution-dialog.component';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { WorkspaceFileService } from '@portal-core/project-files/workspace/services/workspace-file.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export interface CollabPullDialogData {
  branchName: string;
  filePaths: string[];
  licenseId: number;
  projectId: number;
  type: 'doc' | 'workspace';
  userId: string;
}

@Component({
  selector: 'mc-collab-pull-dialog',
  templateUrl: './collab-pull-dialog.component.html',
  styleUrls: ['./collab-pull-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollabPullDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '68rem',
    minHeight: '37rem'
  };

  savingState: LoadingState<string> = new LoadingState<string>();

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: CollabPullDialogData,
    protected dialogRef: MatDialogRef<CollabPullDialogComponent>,
    private collabService: CollabService,
    private errorService: ErrorService,
    private snackBar: MatSnackBar,
    private workspaceFileService: WorkspaceFileService
  ) {
    super(dialog, dialogRef);
  }

  onPullClicked() {
    this.savingState.update(true);

    let pull$: Observable<CollabCommitMergeResult>;

    if (this.data.type === 'doc') {
      pull$ = this.collabService.pullCollabDoc$({
        branchName: this.data.branchName,
        clientId: uuidv4(),
        filePath: this.data.filePaths[0],
        fileType: CollabFileType.Workspace,
        projectId: this.data.projectId,
        userId: this.data.userId,
      });
    } else {
      pull$ = this.workspaceFileService.pullWorkspace$({
        branchName: this.data.branchName,
        clientId: uuidv4(),
        fileType: CollabFileType.Workspace,
        projectId: this.data.projectId,
        userId: this.data.userId,
      });
    }

    pull$.subscribe(result => {
      this.savingState.update(false);

      // If the file was merged (but not fully resolved) then the commit was cancelled and the user needs to review the changes
      if (result?.conflictResolutionResult?.merged?.some(mergeResult => !mergeResult.success)) {
        this.closeDialog(null, true);

        this.dialog.open<CollabConflictResolutionDialogComponent, CollabConflictResolutionDialogData>(CollabConflictResolutionDialogComponent, {
          ...CollabConflictResolutionDialogComponent.DialogConfig,
          data: {
            branchName: this.data.branchName,
            conflictResolutionResult: result.conflictResolutionResult,
            licenseId: this.data.licenseId,
            projectId: this.data.projectId,
            title: this.data.type === 'doc' ? 'The file could not be merged automatically' : 'Some files could not be merged automatically'
          }
        });
      } else {
        const { duration, message } = this.collabService.buildPullResultSnackBarMessage(result);
        this.snackBar.open(message, null, {
          duration,
          panelClass: 'mc-snack-bar-panel-shrink'
        });

        this.closeDialog(true, true);
      }
    }, error => {
      this.savingState.update(false, 'Unable to pull the changes.', this.errorService.getErrorMessages(error))
    });
  }
}
